import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

import { ActivatedRoute } from '@angular/router';
import { AnalysisManagementService } from '../../services/analysis-management.service';
import { AnalysisSequenceItem } from "../sequnce-models/analysisSequenceItem";
import { MapsTypesAvailabilityDto } from '../../dto/MapsTypesAvailabilityDto';
import { MapsTypesAvailabilityModel } from '../../models/mapsTypesAvailabilityModel';
import dayjs from "dayjs";
import { NavBarComponent } from 'src/app/nav-bar/nav-bar.component';
import { MapSyncService } from '../map-sequence-services/map-sync.service';

@Component({
  selector: 'app-analysis-sequence',
  standalone: false,
  templateUrl: './analysis-sequence.component.html',
  styleUrl: './analysis-sequence.component.css',
})
export class AnalysisSequenceComponent implements OnInit {
  @Output() sequenceViewClosed = new EventEmitter<void>();

  protected readonly dayjs = dayjs;
  currentDate = dayjs().format('YYYY-MM-DD');


  analysisSequenceList: AnalysisSequenceItem[] = [];
  analysisTypes: MapsTypesAvailabilityModel[] = [];
  cards = [];

  fieldId: number;
  analysisCount: number = 10;
  analysisDefinitionId: number = 3;
  gridSize: number = 3;

  isNoItemsFound: boolean = false;
  showLegends: boolean = false;

  fromDate: string = '';
  toDate: string = '';
  filterFromDate: string = '';
  filterToDate : string = '';
  currentFieldName: string = '';
  currentFieldSize: string = '';


  constructor(
    private analysisManagementService: AnalysisManagementService,
    private route: ActivatedRoute,
    public router: Router,
    public nav: NavBarComponent,
    private mapSyncService: MapSyncService,
    private location: Location,
  ) { }

  zoomIn(): void {
    this.mapSyncService.zoomAll(0.5);
  }

  zoomOut(): void {
    this.mapSyncService.zoomAll(-0.5);
  }

  resetMaps(): void {
    this.mapSyncService.resetAll();
  }

  toggleLegends(): void {
    this.mapSyncService.toggleLegends();
  }

  ngOnInit(): void {
    this.getFieldInfo();
    this.mapSyncService.resetLegends();
    this.showLegends = false;
    // Set default date range: 5 years ago to today
    this.fromDate = dayjs().subtract(5, 'year').format('YYYY-MM-DD');
    this.toDate = dayjs().format('YYYY-MM-DD');
    this.filterFromDate = this.fromDate;
    this.filterToDate = this.toDate;

    try {
      const fieldIdParam = this.route.snapshot.paramMap.get('fieldId');
      this.fieldId = fieldIdParam ? +fieldIdParam : 0;

      if (this.fieldId > 0) {
        this.loadAnalysisTypes();
      } else {
        console.error('Invalid fieldId');
        this.isNoItemsFound = true;
      }
    } catch (error) {
      console.error('Error initializing analysis sequence:', error);
      this.isNoItemsFound = true;
    }

  }

  loadAnalysisTypes(): void {
    try {
      const previousSelection = this.analysisDefinitionId;
      const mapsTypesAvailabilityDto: MapsTypesAvailabilityDto = {
        farmId: null,
        fieldId: this.fieldId
      };

      this.analysisManagementService.getMapsTypesAvailability(mapsTypesAvailabilityDto).subscribe({
        next: (types) => {
          if (!types || types.length === 0) {
            this.isNoItemsFound = true;
            this.analysisTypes = [];
            this.nav.toggleLoading(false);
            return;
          }

const allowedTypes = ['NDVI SATELLITE', 'NI SATELLITE', 'HS SATELLITE', 'YARA NDVI SATELLITE'];
this.analysisTypes = types.filter(t =>
  t?.available && allowedTypes.includes(t?.type)
);

if (this.analysisTypes.length === 0) {
  this.analysisTypes = [{
    type: 'NO TYPE AVAILABLE',
    alias: 'NO TYPES AVAILABLE',
    displayOrder: -1,
    available: false,
    selected: false
  }];
  this.analysisDefinitionId = -1;
  this.isNoItemsFound = true;
  this.nav.toggleLoading(false);
  return;
}

const previousType = this.analysisTypes.find(t => t.displayOrder === previousSelection);
if (previousType) {
  this.analysisDefinitionId = previousSelection;
} else {
  const hotSpot = this.analysisTypes.find(t => t?.type === 'HS SATELLITE');
  this.analysisDefinitionId = hotSpot ? hotSpot.displayOrder : this.analysisTypes[0].displayOrder;
}

          this.isNoItemsFound = false;
          this.getAnalysisSequenceByFieldIdTypeIdCount(this.fieldId, this.analysisDefinitionId, this.analysisCount, this.fromDate, this.toDate);
        },
        error: (error) => {
          console.error('Error loading analysis types:', error);
          this.isNoItemsFound = true;
          this.analysisTypes = [];
          this.nav.toggleLoading(false);
        }
      });
    } catch (error) {
      console.error('Error in loadAnalysisTypes:', error);
      this.isNoItemsFound = true;
    }
  }

  onAnalysisTypeChange(): void {
    try {
      this.cards = [];
      this.analysisSequenceList = [];
      this.getAnalysisSequenceByFieldIdTypeIdCount(this.fieldId, this.analysisDefinitionId, this.analysisCount, this.fromDate, this.toDate);
    } catch (error) {
      console.error('Error changing analysis type:', error);
    }
  }

  onCountChange(): void {
    try {
      this.cards = [];
      this.analysisSequenceList = [];
      this.getAnalysisSequenceByFieldIdTypeIdCount(this.fieldId, this.analysisDefinitionId, this.analysisCount, this.fromDate, this.toDate);
    } catch (error) {
      console.error('Error changing count:', error);
    }
  }

  getAnalysisSequenceByFieldIdTypeIdCount(fieldId: number, analysisDefinitionId: number, analysisCount: number, fromDate?: string, toDate?: string): void {
    try {
      if (!fieldId || fieldId <= 0) {
        console.error('Invalid fieldId');
        this.isNoItemsFound = true;
        this.nav.toggleLoading(false);
        return;
      }

      this.analysisManagementService
        .getAnalysisSequenceByFieldIdTypeIdCount(fieldId, analysisDefinitionId, analysisCount, fromDate, toDate)
        .subscribe({
          next: (result) => {
            if (!result || result.length === 0) {
              this.isNoItemsFound = true;
              this.cards = [];
              this.analysisSequenceList = [];
              return;
            }

            this.isNoItemsFound = false;
            this.analysisSequenceList = result;
            this.cards = result.map(item => ({
              title: item?.type || 'Unknown',
              content: item?.datePerformed || '',
              fieldId: this.fieldId,
              analysisId: item?.analysisId,
              propertyTypeId: item?.propertyTypeId,
              mapUrl: item?.mapUrl || '',
              latMin: item?.latMin,
              lngMin: item?.lngMin,
              latMax: item?.latMax,
              lngMax: item?.lngMax
            }));
          },
        error: (error) => {
          console.error('Error fetching analysis sequence:', error);
          this.isNoItemsFound = true;
          this.cards = [];
          this.analysisSequenceList = [];
          this.nav.toggleLoading(false);
        },
        complete: () => this.nav.toggleLoading(false),
      });
    } catch (error) {
      console.error('Error in getAnalysisSequenceByFieldIdTypeIdCount:', error);
      this.isNoItemsFound = true;
      this.nav.toggleLoading(false);
    }
  }

  closeSequenceView(): void {
    try {
      this.sequenceViewClosed.emit();
      this.location.back();
    } catch (error) {
      console.error('Error closing sequence view:', error);
      this.router.navigate(['field']);
    }
  }

  setFilterStartDate(value: string | Date): void {
    this.fromDate = value ? dayjs(value).format('YYYY-MM-DD') : '';

    if (this.fromDate && this.toDate) {
      this.loadAnalysisTypes();
    }
  }

  setFilterEndDate(value: string | Date): void {
    this.toDate = value ? dayjs(value).format('YYYY-MM-DD') : '';

    if (this.fromDate && this.toDate) {
      this.loadAnalysisTypes();
    }
  }

  getFieldInfo() {
    const fieldData = sessionStorage.getItem('currentFieldInfo');

    let fieldObj = JSON.parse(fieldData);
    this.currentFieldName = fieldObj?.fieldName;
    this.currentFieldSize = fieldObj?.fieldSize;
  }
}
